import type { Input } from '../engine/input';
import {
  type Settings,
  type BindableAction,
  ACTION_LABELS,
  SENS_MIN,
  SENS_MAX,
  keyLabel,
  saveSettings,
  resetSettings,
} from '../settings';
import { clearAccount } from '../net';
import { CAMERA, HEADBOB, IS_DEV } from '../config';
import { SK, btnCss, kbd, labelCss, titleCss, panelCss, redHover, injectSkinCss } from './skin';

// Rebindable controls, grouped for the settings page. Actions not listed here (leanLeft/leanRight -- peek
// was removed) are simply not shown. The Developer group only renders on localhost (IS_DEV).
const CONTROL_GROUPS: Array<{ title: string; actions: BindableAction[]; devOnly?: boolean }> = [
  { title: 'Movement', actions: ['forward', 'back', 'left', 'right', 'sprint', 'crouch', 'jump'] },
  { title: 'Actions', actions: ['interact', 'inventory', 'crafting', 'build', 'buildHeight', 'buildAlt'] },
  { title: 'Hotbar', actions: ['hotbar1', 'hotbar2', 'hotbar3', 'hotbar4', 'hotbar5', 'hotbar6'] },
  { title: 'Developer', actions: ['debug'], devOnly: true },
];

// Pause menu, opened with Escape. Two views: the MAIN menu (Resume / Settings / Exit to Server List) and a
// SETTINGS page (camera, audio, and conflict-free rebindable controls incl. the hotbar). While open,
// gameplay input is suppressed so the player stands still. Changes persist via Settings (localStorage).
export class Menu {
  open = false;
  // Set by main to open the controls/help overlay; kept as a settable callback (rather than a constructor
  // dependency) so Menu and Help can be constructed in either order without a circular import.
  onOpenHelp: (() => void) | null = null;
  // Live connection readout for the pause menu (owner ask): main supplies current ping + link state. null = hide it
  // (e.g. the dev offline mode). Polled once a second while the menu is open.
  netStatus: (() => { ping: number; connected: boolean }) | null = null;
  private netTimer = 0;
  private readonly root: HTMLDivElement;
  private readonly panel: HTMLDivElement;
  private capturing: { action: BindableAction; index: number } | null = null;
  private view: 'main' | 'settings' = 'main';

  constructor(
    private readonly settings: Settings,
    private readonly input: Input,
    private readonly onCompression: (fov: number) => void,
    private readonly onFpFov: (fov: number) => void,
    private readonly onVolume: (v: number) => void,
    // Wipes the local world save and reboots into a fresh world; undefined (MP / test scenes) hides the button.
    private readonly onFreshStart?: () => void,
  ) {
    injectSkinCss();
    this.root = document.createElement('div');
    this.root.style.cssText = [
      'position:fixed', 'inset:0', 'z-index:20', 'display:none',
      'align-items:center', 'justify-content:center',
      `background:${SK.scrim}`,
      `font-family:${SK.font}`,
    ].join(';');

    // Menu screen (no world to keep readable behind it) = the opaque menu panel.
    this.panel = document.createElement('div');
    this.panel.style.cssText = [
      'width:min(430px,92vw)', 'max-height:86vh', 'overflow-y:auto',
      panelCss('menu'), 'padding:24px', 'box-shadow:0 18px 50px rgba(0,0,0,0.6)',
    ].join(';');
    this.root.appendChild(this.panel);
    document.body.appendChild(this.root);

    // Clicking the dim backdrop (not the panel) resumes.
    this.root.addEventListener('pointerdown', (e) => {
      if (e.target === this.root) this.close();
    });

    this.render();
  }

  // Escape: open the MAIN pause menu from gameplay; from the Settings sub-page, go BACK to the main menu
  // (never resume straight out of Settings, and never open onto the control panel). From the main menu, resume.
  toggle(): void {
    if (!this.open) { this.show(); return; }
    if (this.view === 'settings') { this.view = 'main'; this.render(); return; }
    this.close();
  }

  private show(): void {
    this.open = true;
    this.view = 'main'; // always open on the main menu, not a stale settings page
    this.root.style.display = 'flex';
    this.input.setSuppressed(true);
    this.render();
    if (this.netStatus && !this.netTimer) { this.updateNet(); this.netTimer = window.setInterval(() => this.updateNet(), 1000); }
  }

  close(): void {
    this.open = false;
    this.view = 'main';
    this.root.style.display = 'none';
    this.capturing = null;
    if (this.netTimer) { clearInterval(this.netTimer); this.netTimer = 0; }
    this.input.cancelCapture();
    this.input.setSuppressed(false);
  }

  // Refresh the pause-menu connection line (ping + link state), color-coded. Cheap; runs ~1Hz while open.
  private updateNet(): void {
    const el = document.getElementById('ox-net-status');
    if (!el || !this.netStatus) return;
    const s = this.netStatus();
    if (!s.connected) { el.textContent = 'Reconnecting to server…'; el.style.color = SK.bad; return; }
    const ping = s.ping < 0 ? '…' : `${s.ping} ms`;
    const col = s.ping < 0 ? SK.dim : s.ping < 120 ? SK.good : s.ping < 250 ? SK.warn : SK.bad;
    el.textContent = `Connected · ping ${ping}`;
    el.style.color = col;
  }

  private startCapture(action: BindableAction, index: number): void {
    this.capturing = { action, index };
    this.render();
    this.input.beginCaptureRebind(action, index, () => {
      this.capturing = null;
      this.render();
    });
  }

  private render(): void {
    this.panel.replaceChildren();
    this.panel.scrollTop = 0;
    if (this.view === 'settings') this.renderSettings();
    else this.renderMain();
  }

  // MAIN pause menu: three primary actions only (owner ask). Everything configurable lives under Settings.
  private renderMain(): void {
    const p = this.panel;
    p.appendChild(text('div', 'OXIDE', titleCss()));
    const sub = text('div', '', `color:${SK.dim};font-size:12px;margin:4px 0 12px;`);
    sub.innerHTML = `Paused. ${kbd('Esc')} to resume.`;
    p.appendChild(sub);
    if (this.netStatus) { p.appendChild(text('div', 'Connected · ping …', `color:${SK.dim};font-size:12px;margin:0 0 16px;font-variant-numeric:tabular-nums;`)).id = 'ox-net-status'; }
    p.appendChild(button('Resume', () => this.close(), 'primary', 'width:100%;margin-bottom:8px;'));
    p.appendChild(button('Settings', () => { this.view = 'settings'; this.render(); }, 'secondary', 'width:100%;margin-bottom:8px;'));
    p.appendChild(button('Exit to Server List', () => {
      // One-shot flag so the reload lands on the MAIN MENU even when dev auto-spawn is on (localhost). Main
      // reads + clears it on boot, so normal reloads keep auto-spawning into the game.
      try { sessionStorage.setItem('oxide.forceMenu', '1'); } catch { /* ignore */ }
      location.reload();
    }, 'secondary', 'width:100%;'));
  }

  // SETTINGS page: a back arrow, then Camera / Audio / Controls (conflict-free rebinding, incl. hotbar 1-6)
  // / Account. Reachable only from the main menu's Settings button.
  private renderSettings(): void {
    const p = this.panel;
    const head = div('display:flex;align-items:center;gap:12px;margin-bottom:16px;');
    head.appendChild(button('‹ Back', () => { this.view = 'main'; this.render(); }, 'secondary', '', true));
    head.appendChild(text('div', 'SETTINGS', titleCss(16)));
    p.appendChild(head);

    const helpBtn = button('', () => this.onOpenHelp?.(), 'secondary', 'width:100%;margin-bottom:12px;');
    helpBtn.innerHTML = `Controls / Help sheet ${kbd('H')}`;
    p.appendChild(helpBtn);

    p.appendChild(sectionHeader('Movement'));
    p.appendChild(segmentRow<'hold' | 'toggle' | 'auto'>('Sprint', this.settings.sprintMode,
      [{ v: 'hold', label: 'Hold' }, { v: 'toggle', label: 'Toggle' }, { v: 'auto', label: 'Auto' }],
      (v) => { this.settings.sprintMode = v; saveSettings(this.settings); }));
    p.appendChild(text('div', 'Toggle / Auto mean you never HOLD Shift — which is what makes the browser context menu pop on Shift + right-click while aiming.', `color:${SK.dim};font-size:12px;margin:4px 0 4px;`));

    p.appendChild(sectionHeader('Camera'));
    p.appendChild(toggleRow('Invert vertical look (middle-mouse)', this.settings.invertMmbY, (v) => { this.settings.invertMmbY = v; saveSettings(this.settings); }));
    p.appendChild(toggleRow('Invert vertical look (Alt free-look)', this.settings.invertAltY, (v) => { this.settings.invertAltY = v; saveSettings(this.settings); }));
    p.appendChild(sliderRow('Look sensitivity', this.settings.lookSensitivity, SENS_MIN, SENS_MAX, (v) => { this.settings.lookSensitivity = v; saveSettings(this.settings); }));
    p.appendChild(sliderRow('ADS sensitivity (while aiming)', this.settings.adsSensitivity, 0.2, 2, (v) => { this.settings.adsSensitivity = v; saveSettings(this.settings); }));
    p.appendChild(sliderRow('Spatial compression (depth)', this.settings.spatialCompression, CAMERA.fovMin, CAMERA.fovMax, (v) => { this.settings.spatialCompression = v; saveSettings(this.settings); this.onCompression(v); }, '', 1, 0));
    p.appendChild(sliderRow('First-person FOV', this.settings.fpFov, CAMERA.fpFovMin, CAMERA.fpFovMax, (v) => { this.settings.fpFov = v; saveSettings(this.settings); this.onFpFov(v); }, '', 1, 0));
    p.appendChild(toggleRow('First-person head-bob (walk/sprint)', this.settings.headbob, (v) => { this.settings.headbob = v; HEADBOB.enabled = v; saveSettings(this.settings); }));

    p.appendChild(sectionHeader('Audio'));
    p.appendChild(sliderRow('Master volume', this.settings.volume, 0, 1, (v) => { this.settings.volume = v; saveSettings(this.settings); this.onVolume(v); }, '', 0.05, 2));

    p.appendChild(sectionHeader('Controls'));
    p.appendChild(text('div', 'Click a slot, then press a key or mouse button (side buttons 4/5 work too). A key can only be bound to one action — assigning it here removes it from any other. Middle-mouse (orbit) and Alt (free-look) are fixed.', `color:${SK.dim};font-size:12px;margin-bottom:12px;`));
    for (const group of CONTROL_GROUPS) {
      if (group.devOnly && !IS_DEV) continue; // the debug bind is meaningless (and hidden) off localhost
      p.appendChild(text('div', group.title, labelCss() + ';margin:12px 0 4px;'));
      for (const action of group.actions) p.appendChild(this.controlRow(action));
    }

    p.appendChild(sectionHeader('Account'));
    p.appendChild(button('Reset controls & settings to defaults', () => { resetSettings(this.settings); this.input.rebuildBindings(); this.render(); }, 'secondary', 'width:100%;margin-bottom:8px;'));
    if (this.onFreshStart) {
      p.appendChild(button('Fresh start (wipe local save)', () => { if (window.confirm('Delete this world’s local save and start over? This cannot be undone.')) this.onFreshStart!(); }, 'danger', 'width:100%;margin-bottom:8px;'));
    }
    p.appendChild(button('Log out', () => { clearAccount(); location.reload(); }, 'ghost', 'width:100%;'));
  }

  private controlRow(action: BindableAction): HTMLElement {
    const row = div('display:flex;align-items:center;justify-content:space-between;gap:8px;padding:4px 0;');
    row.appendChild(text('span', ACTION_LABELS[action], `color:${SK.text};font-size:13px;`));

    const keys = div('display:flex;gap:6px;flex-wrap:wrap;justify-content:flex-end;');
    this.settings.binds[action].forEach((code, i) => {
      const active = this.capturing?.action === action && this.capturing.index === i;
      // The slot being rebound reads as SELECTED: accent border + accent-soft fill.
      const chip = button(
        active ? 'press key/mouse...' : keyLabel(code),
        () => this.startCapture(action, i),
        'secondary',
        active ? `background:${SK.accentSoft};border-color:${SK.accent};color:${SK.textHi};` : '',
        true,
      );
      keys.appendChild(chip);
    });
    row.appendChild(keys);
    // Warn when a binding uses Ctrl: browsers reserve Ctrl+W (which is crouch + forward here) and can close
    // the tab. C is the default crouch; this only nudges the user off Ctrl.
    const usesCtrl = this.settings.binds[action].some((c) => c === 'ControlLeft' || c === 'ControlRight');
    if (!usesCtrl) return row;
    const wrap = div('display:flex;flex-direction:column;');
    wrap.appendChild(row);
    wrap.appendChild(text('div', '⚠ Ctrl can close the tab (Ctrl+W). C crouches by default.', `color:${SK.warn};font-size:11px;text-align:right;padding:0 0 4px;`));
    return wrap;
  }
}

// --- small DOM helpers ---

function div(css: string): HTMLDivElement {
  const el = document.createElement('div');
  el.style.cssText = css;
  return el;
}

function text(tag: string, content: string, css: string): HTMLElement {
  const el = document.createElement(tag);
  el.textContent = content;
  el.style.cssText = css;
  return el;
}

// btnCss kinds per the style guide: ONE primary per view (Resume), secondary outlines for the rest,
// ghost for tertiary, danger = red OUTLINE. Danger keeps its semantic border (no accent hover repaint).
function button(label: string, onClick: () => void, kind: 'primary' | 'secondary' | 'ghost' | 'danger' = 'secondary', extra = '', small = false): HTMLButtonElement {
  const b = document.createElement('button');
  b.textContent = label;
  b.style.cssText = btnCss(kind, small) + ';' + extra;
  b.addEventListener('pointerdown', (e) => e.stopPropagation());
  b.addEventListener('click', onClick);
  if (kind !== 'primary' && kind !== 'danger') redHover(b); // hover = accent-soft fill + accent border
  return b;
}

function sectionHeader(title: string): HTMLElement {
  return text(
    'div',
    title,
    labelCss() + `;margin:16px 0 8px;border-bottom:1px solid ${SK.border};padding-bottom:4px;`,
  );
}

function toggleRow(label: string, value: boolean, onChange: (v: boolean) => void): HTMLElement {
  const row = document.createElement('label');
  row.style.cssText = 'display:flex;align-items:center;justify-content:space-between;gap:12px;padding:4px 0;cursor:pointer;';
  row.appendChild(text('span', label, `color:${SK.text};font-size:13px;`));
  const cb = document.createElement('input');
  cb.type = 'checkbox';
  cb.checked = value;
  cb.style.cssText = `width:16px;height:16px;cursor:pointer;accent-color:${SK.accent};`;
  cb.addEventListener('pointerdown', (e) => e.stopPropagation());
  cb.addEventListener('change', () => onChange(cb.checked));
  row.appendChild(cb);
  return row;
}

// A labelled 3+ way segmented selector (pills), for enum settings like the sprint mode.
function segmentRow<T extends string>(label: string, value: T, options: Array<{ v: T; label: string }>, onChange: (v: T) => void): HTMLElement {
  const row = document.createElement('div');
  row.style.cssText = 'display:flex;align-items:center;justify-content:space-between;gap:12px;padding:4px 0;';
  row.appendChild(text('span', label, `color:${SK.text};font-size:13px;`));
  const group = document.createElement('div');
  group.style.cssText = 'display:flex;gap:4px;';
  const btns: HTMLButtonElement[] = [];
  // Selected pill = accent border + accent-soft fill (selection language); the rest stay neutral outlines.
  const paint = (sel: T): void => {
    options.forEach((opt, i) => {
      const on = opt.v === sel;
      btns[i].style.background = on ? SK.accentSoft : 'transparent';
      btns[i].style.color = on ? SK.textHi : SK.text;
      btns[i].style.borderColor = on ? SK.accent : SK.borderStrong;
    });
  };
  for (const opt of options) {
    const b = document.createElement('button');
    b.textContent = opt.label;
    b.style.cssText = btnCss('secondary', true);
    b.addEventListener('pointerdown', (e) => e.stopPropagation());
    b.addEventListener('click', () => { onChange(opt.v); paint(opt.v); });
    group.appendChild(b);
    btns.push(b);
  }
  paint(value);
  row.appendChild(group);
  return row;
}

function sliderRow(
  label: string,
  value: number,
  min: number,
  max: number,
  onChange: (v: number) => void,
  suffix = 'x',
  step = 0.05,
  digits = 2,
): HTMLElement {
  const row = div('padding:8px 0;');
  const top = div(`display:flex;justify-content:space-between;margin-bottom:4px;color:${SK.text};font-size:13px;`);
  top.appendChild(text('span', label, ''));
  const readout = text('span', value.toFixed(digits) + suffix, `color:${SK.dim};font-variant-numeric:tabular-nums;`);
  top.appendChild(readout);
  row.appendChild(top);

  const slider = document.createElement('input');
  slider.type = 'range';
  slider.min = String(min);
  slider.max = String(max);
  slider.step = String(step);
  slider.value = String(value);
  slider.style.cssText = `width:100%;cursor:pointer;accent-color:${SK.accent};`;
  slider.addEventListener('pointerdown', (e) => e.stopPropagation());
  slider.addEventListener('input', () => {
    const v = parseFloat(slider.value);
    readout.textContent = v.toFixed(digits) + suffix;
    onChange(v);
  });
  row.appendChild(slider);
  return row;
}
